/**
 * Трёхстороннее слияние при конфликте версий (409) — до совместного редактирования в реальном времени (фаза 4).
 *
 * base — версия, от которой шла правка (последняя известная клиенту), mine — локальная, theirs — текущая на сервере.
 * Элементы с id (блоки документа, узлы и рёбра карт и досок) сливаются поштучно: если элемент менял только один
 * участник — берём его вариант; если оба и по-разному — это конфликт (в результате остаётся серверный вариант).
 * Порядок — серверный; новые локальные элементы встают после своего соседа слева.
 */

type Any = any;
export type Keyed = { id: string };
export type MergeResult<T> = { result: T; conflicts: string[] };

/** Сравнение JSON без учёта порядка ключей. */
export function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    return a.length === bb.length && a.every((x, i) => same(x, bb[i]));
  }
  const ka = Object.keys(a as object).filter((k) => (a as Any)[k] !== undefined);
  const kb = Object.keys(b as object).filter((k) => (b as Any)[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => same((a as Any)[k], (b as Any)[k]));
}

export function mergeKeyed<T extends Keyed>(
  base: T[],
  mine: T[],
  theirs: T[],
  prefix = '',
  /** Слияние одного элемента, который правили обе стороны (например, по полям); без него — конфликт. */
  mergeItem?: (b: T, m: T, t: T) => MergeResult<T>,
): MergeResult<T[]> {
  const B = new Map(base.map((x) => [x.id, x]));
  const M = new Map(mine.map((x) => [x.id, x]));
  const T = new Map(theirs.map((x) => [x.id, x]));
  const conflicts: string[] = [];
  const pick = new Map<string, T | null>(); // null — удалён
  for (const id of new Set([...B.keys(), ...M.keys(), ...T.keys()])) {
    const b = B.get(id),
      m = M.get(id),
      t = T.get(id);
    const mChanged = b ? !m || !same(b, m) : !!m;
    const tChanged = b ? !t || !same(b, t) : !!t;
    if (mChanged && tChanged) {
      const both = b && m && t && mergeItem ? mergeItem(b, m, t) : null;
      if ((!m && !t) || (m && t && same(m, t))) pick.set(id, m ?? null);
      else if (both && !both.conflicts.length) pick.set(id, both.result);
      else {
        conflicts.push(prefix + id);
        pick.set(id, t ?? null);
      }
    } else if (mChanged) pick.set(id, m ?? null);
    else pick.set(id, t ?? null);
  }
  const result: T[] = theirs.filter((x) => pick.get(x.id)).map((x) => pick.get(x.id)!);
  // Новые (и восстановленные мной) элементы, которых нет на сервере, — после ближайшего соседа слева из моей версии.
  mine.forEach((x, i) => {
    if (T.has(x.id) || !pick.get(x.id)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const k = result.findIndex((y) => y.id === mine[j]!.id);
      if (k >= 0) {
        at = k + 1;
        break;
      }
    }
    result.splice(at, 0, pick.get(x.id)!);
  });
  return { result, conflicts };
}

/** Поля объекта (оформление, свойства страницы) — по ключам. */
export function mergeRecord(base: Any, mine: Any, theirs: Any, prefix = ''): MergeResult<Any> {
  const b = base ?? {},
    m = mine ?? {},
    t = theirs ?? {};
  const result: Any = {};
  const conflicts: string[] = [];
  for (const k of new Set([...Object.keys(b), ...Object.keys(m), ...Object.keys(t)])) {
    const mChanged = !same(b[k], m[k]),
      tChanged = !same(b[k], t[k]);
    const v = mChanged && !tChanged ? m[k] : t[k];
    if (mChanged && tChanged && !same(m[k], t[k])) conflicts.push(prefix + k);
    if (v !== undefined) result[k] = v;
  }
  return { result, conflicts };
}

function mergeScalar<V>(base: V, mine: V, theirs: V, name: string, conflicts: string[]): V {
  if (same(mine, base)) return theirs;
  if (same(theirs, base) || same(mine, theirs)) return mine;
  conflicts.push(name);
  return theirs;
}

export type DocState = { title: string; blocks: Keyed[]; page: Any };

export function mergeDoc(base: DocState, mine: DocState, theirs: DocState): MergeResult<DocState> {
  // Документ ещё ни разу не сохранялся блоками (собран из Markdown у каждого заново, id блоков разные):
  // поштучно слить нельзя — всё содержимое считается конфликтом.
  if (!base.blocks.length && mine.blocks.length && theirs.blocks.length && !same(mine.blocks, theirs.blocks)) {
    return { result: { ...theirs }, conflicts: ['blocks'] };
  }
  const conflicts: string[] = [];
  const blocks = mergeKeyed(base.blocks, mine.blocks, theirs.blocks, 'block:');
  const page = mergeRecord(base.page, mine.page, theirs.page, 'page:');
  const title = mergeScalar(base.title, mine.title, theirs.title, 'title', conflicts);
  return {
    result: { title, blocks: blocks.result, page: page.result },
    conflicts: [...conflicts, ...blocks.conflicts, ...page.conflicts],
  };
}

/** Узел, который правили обе стороны: по полям, `data` — по ключам (переместил один, переименовал другой — не конфликт). */
export function mergeNode(b: Any, m: Any, t: Any): MergeResult<Any> {
  const top = mergeRecord({ ...b, data: null }, { ...m, data: null }, { ...t, data: null });
  const data = mergeRecord(b.data, m.data, t.data);
  return { result: { ...top.result, data: data.result }, conflicts: [...top.conflicts, ...data.conflicts] };
}

/**
 * Карта и доска: узлы, рёбра, связи, рамки, сводки, оформление. Висячие рёбра после слияния убираются.
 * layout — положения считает автораскладка (майнд-карта не в свободном режиме): их не сравниваем, берём мои.
 */
export function mergeGraph(base: Any, mine: Any, theirs: Any, opts: { layout?: boolean } = {}): MergeResult<Any> {
  const conflicts: string[] = [];
  const noPos = (c: Any) =>
    opts.layout ? { ...c, nodes: (c?.nodes ?? []).map(({ position: _, ...n }: Any) => n) } : c;
  const [B, M, T] = [noPos(base), noPos(mine), noPos(theirs)];
  const keyed = (k: string) => {
    const r = mergeKeyed(B?.[k] ?? [], M?.[k] ?? [], T?.[k] ?? [], k + ':', k === 'nodes' ? mergeNode : undefined);
    conflicts.push(...r.conflicts);
    return r.result;
  };
  let nodes = keyed('nodes');
  if (opts.layout) {
    const pos = new Map([...(theirs?.nodes ?? []), ...(mine?.nodes ?? [])].map((n: Any) => [n.id, n.position]));
    nodes = nodes.map((n: Any) => ({ ...n, position: pos.get(n.id) ?? { x: 0, y: 0 } }));
  }
  const ids = new Set(nodes.map((n: Any) => n.id));
  const linked = (e: Any) => ids.has(e.source) && ids.has(e.target) && e.source !== e.target;
  const style = mergeRecord(base?.style, mine?.style, theirs?.style, 'style:');
  conflicts.push(...style.conflicts);
  const result: Any = { ...theirs, nodes, edges: keyed('edges').filter(linked), style: style.result };
  if (!base?.style && !mine?.style && !theirs?.style) delete result.style;
  for (const k of ['rels', 'groups', 'summaries']) {
    if (!base?.[k] && !mine?.[k] && !theirs?.[k]) continue;
    const list = keyed(k);
    result[k] =
      k === 'rels'
        ? list.filter(linked)
        : list
            .map((g: Any) => ({ ...g, nodes: (g.nodes ?? []).filter((n: string) => ids.has(n)) }))
            .filter((g: Any) => g.nodes.length);
  }
  return { result, conflicts };
}
